// Plain-TypeScript replacement for the jquery.contextmenu plugin's menu widget. Renders a single
// <ul role="menu"> appended to <body>; only one menu is ever open at a time.

import { placeMenu } from './position';

export interface MenuItem {
  label: string;
  icon?: string; // matches CSS class context-menu-icon-<icon>; omit for no icon
  run: () => void;
}

export type MenuEntry = MenuItem | { separator: true };

export interface OpenMenuOptions {
  x: number;
  y: number;
  items: MenuEntry[];
  returnFocusTo?: HTMLElement | null;
}

interface OpenMenu {
  list: HTMLUListElement;
  cleanup: () => void;
  returnFocusTo?: HTMLElement | null;
}

let current: OpenMenu | null = null;

export function closeMenu(): void {
  if (!current) return;
  const { list, cleanup, returnFocusTo } = current;
  current = null;
  cleanup();
  list.remove();
  if (returnFocusTo && document.contains(returnFocusTo)) {
    returnFocusTo.focus();
  }
}

export function openMenu(options: OpenMenuOptions): void {
  closeMenu();

  const list = document.createElement('ul');
  list.className = 'context-menu-list';
  list.setAttribute('role', 'menu');

  const itemEls: HTMLLIElement[] = [];
  for (const entry of options.items) {
    const li = document.createElement('li');
    if ('separator' in entry) {
      li.className = 'context-menu-separator';
      li.setAttribute('role', 'separator');
    } else {
      // The icon glyph's ::before positioning rule targets the base 'context-menu-icon' class;
      // the specific 'context-menu-icon-<name>' class only supplies the glyph's content.
      li.className = entry.icon ? `context-menu-item context-menu-icon context-menu-icon-${entry.icon}` : 'context-menu-item';
      li.setAttribute('role', 'menuitem');
      li.tabIndex = -1;
      li.textContent = entry.label;
      li.addEventListener('click', () => {
        entry.run();
        closeMenu();
      });
      itemEls.push(li);
    }
    list.appendChild(li);
  }

  document.body.appendChild(list);
  placeMenu(list, options.x, options.y);


  function focusItem(index: number): void {
    const target = itemEls[index];
    if (!target) return;
    for (const el of itemEls) el.tabIndex = -1;
    target.tabIndex = 0;
    target.focus();
  }

  function currentIndex(): number {
    return itemEls.findIndex((el) => el === document.activeElement);
  }

  function onKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusItem((currentIndex() + 1 + itemEls.length) % itemEls.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusItem((currentIndex() - 1 + itemEls.length) % itemEls.length);
        break;
      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;
      case 'End':
        e.preventDefault();
        focusItem(itemEls.length - 1);
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const idx = currentIndex();
        if (idx >= 0) {
          const entry = options.items.filter((i): i is MenuItem => !('separator' in i))[idx];
          entry.run();
        }
        closeMenu();
        break;
      }
      case 'Escape':
        e.preventDefault();
        closeMenu();
        break;
      case 'Tab':
        closeMenu();
        break;
      default:
        break;
    }
  }

  function onOutsidePointerDown(e: PointerEvent): void {
    if (!(e.target instanceof Node) || !list.contains(e.target)) closeMenu();
  }

  function onDismiss(): void {
    closeMenu();
  }

  list.addEventListener('keydown', onKeydown);
  document.addEventListener('pointerdown', onOutsidePointerDown, true);
  window.addEventListener('scroll', onDismiss, true);
  window.addEventListener('resize', onDismiss);

  current = {
    list,
    returnFocusTo: options.returnFocusTo,
    cleanup: () => {
      list.removeEventListener('keydown', onKeydown);
      document.removeEventListener('pointerdown', onOutsidePointerDown, true);
      window.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
    },
  };

  if (itemEls.length > 0) focusItem(0);
}
